import { useEffect, useRef } from 'react';
import { useAppStore } from '@/stores/useAppStore';
import { useGroupStore } from '@/stores/useGroupStore';
import { gamificationService } from '@/services/gamificationService';

export const useGamificationSync = () => {
  const { userBadges, challenge, goals } = useAppStore();
  const { myGroups } = useGroupStore();
  
  const previousBadges = useRef(userBadges.length);
  const previousStreak = useRef(challenge?.streak || 0);
  const previousCompletedGoals = useRef(goals.filter(g => g.status === 'concluido').length);
  const previousCheckinDates = useRef(challenge?.completedDates.length || 0);

  // Detectar novo badge
  useEffect(() => {
    if (userBadges.length > previousBadges.current) {
      const newBadge = userBadges[userBadges.length - 1];
      
      myGroups.forEach(group => {
        gamificationService.createActivityWithPoints({
          groupId: group.id,
          tipo: 'badge',
          payload: {
            badge_id: newBadge.badgeId,
            badge_name: newBadge.badgeId,
            earned_at: newBadge.earnedAt
          },
          eventType: 'badge_earned'
        });
      });
    }
    previousBadges.current = userBadges.length;
  }, [userBadges, myGroups]);

  // Detectar marcos de streak
  useEffect(() => {
    const currentStreak = challenge?.streak || 0;
    const streakMilestones = [7, 21, 66, 90];
    
    const newMilestone = streakMilestones.find(
      milestone => currentStreak >= milestone && previousStreak.current < milestone
    );
    
    if (newMilestone) {
      myGroups.forEach(group => {
        gamificationService.createActivityWithPoints({
          groupId: group.id,
          tipo: 'streak',
          payload: {
            streak: currentStreak,
            milestone: newMilestone
          },
          eventType: `streak_${newMilestone}`
        });
      });
    }
    
    previousStreak.current = currentStreak;
  }, [challenge?.streak, myGroups]);

  // Detectar meta completada
  useEffect(() => {
    const currentCompleted = goals.filter(g => g.status === 'concluido').length;
    
    if (currentCompleted > previousCompletedGoals.current) {
      const newlyCompleted = goals.find(
        g => g.status === 'concluido' && !previousCompletedGoals.current
      );
      
      if (newlyCompleted) {
        myGroups.forEach(group => {
          gamificationService.createActivityWithPoints({
            groupId: group.id,
            tipo: 'meta_concluida',
            payload: {
              goal_title: newlyCompleted.title,
              pillar: newlyCompleted.pillar
            },
            eventType: 'goal_completed'
          });
        });
      }
    }
    
    previousCompletedGoals.current = currentCompleted;
  }, [goals, myGroups]);

  // Detectar check-in diário
  useEffect(() => {
    const currentCheckins = challenge?.completedDates.length || 0;
    
    if (currentCheckins > previousCheckinDates.current) {
      const latestDate = challenge?.completedDates[currentCheckins - 1];
      
      myGroups.forEach(group => {
        gamificationService.createActivityWithPoints({
          groupId: group.id,
          tipo: 'checkin',
          payload: {
            date: latestDate,
            day_number: currentCheckins,
            total_days: 90
          },
          eventType: 'daily_checkin'
        });
      });
    }
    
    previousCheckinDates.current = currentCheckins;
  }, [challenge?.completedDates, myGroups]);
};
